import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Flag } from "lucide-react";
import PillToggle from "@/components/common/PillToggle";
import GradientBar from "@/components/common/GradientBar";
import ErrorInline from "@/components/common/ErrorInline";
import PhaseStrip from "./PhaseStrip";
import TimelineCanvas from "./TimelineCanvas";
import PhasesView from "./PhasesView";
import { getRoadmap } from "@/lib/roadmapApi";

/** Phase D: real roadmap from /api/roadmap. UI preserved. */
export default function RoadmapPage() {
  const [view, setView] = useState("timeline"); // "timeline" | "phases"
  const [rm, setRm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setError(null);
    try {
      const data = await getRoadmap();
      setRm(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Couldn't load roadmap");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Adapt snake_case API into the camelCase shape the existing components expect.
  const adapted = useMemo(() => {
    if (!rm) return null;
    const phases = (rm.phases || []).map((p) => ({
      id: p.id,
      name: p.name,
      weeks: p.weeks,
      startDay: p.start_day,
      endDay: p.end_day,
      color: p.color,
      status: p.status,
      tagline: p.tagline,
    }));
    const tasks = (rm.tasks || []).map((t) => ({
      id: t.id,
      phaseId: t.phase_id,
      title: t.title,
      owner: t.owner,
      priority: t.priority,
      startDay: t.start_day,
      durationDays: t.duration_days,
      progress: t.progress,
      status: t.status,
    }));
    const milestones = (rm.milestones || []).map((m) => ({
      id: m.id,
      day: m.day,
      label: m.label,
      status: m.status,
    }));
    return {
      phases,
      tasks,
      milestones,
      currentDay: rm.current_day,
      totalDays: rm.total_days,
      onTrackPercent: rm.on_track_percent,
    };
  }, [rm]);

  const atRiskCount = useMemo(
    () => (adapted?.milestones || []).filter((m) => m.status === "at_risk").length,
    [adapted],
  );

  return (
    <div data-testid="roadmap-page" className="px-5 pb-16 pt-6 sm:px-8 sm:pt-8 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end md:gap-6"
      >
        <div className="min-w-0">
          <div className="eyebrow">Execution Timeline</div>
          <h1 className="mt-2 text-[40px] font-extrabold leading-[1] tracking-tight text-zinc-900 sm:text-[56px] lg:text-[72px]">
            90-Day Roadmap
          </h1>
          <p className="mt-2 text-[13px] text-zinc-500 sm:text-[14px]">
            From idea to first 100 customers · Day 1 — Day 90
          </p>
        </div>

        <div className="flex flex-shrink-0 items-center gap-3">
          <PillToggle
            testId="roadmap-view-toggle"
            value={view}
            onChange={setView}
            options={[
              { value: "timeline", label: "Timeline" },
              { value: "phases", label: "Phases" },
            ]}
          />
        </div>
      </motion.div>

      {loading && !rm ? (
        <div
          data-testid="roadmap-loading"
          className="mt-8 card-soft rounded-[28px] p-10 text-center text-[13px] text-zinc-500"
        >
          Loading roadmap…
        </div>
      ) : error && !adapted ? (
        <div className="mt-8">
          <ErrorInline message={error} onRetry={refresh} />
        </div>
      ) : !adapted ? null : (
        <>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div
              data-testid="stat-current-day"
              className="card-soft inline-flex items-center gap-2 rounded-full px-4 py-2"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-500/40" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-600" />
              </span>
              <CalendarDays className="h-3.5 w-3.5 text-zinc-500" strokeWidth={2.2} />
              <span className="text-[12.5px] font-semibold text-zinc-800">
                Day {adapted.currentDay} of {adapted.totalDays}
              </span>
            </div>

            <div
              data-testid="stat-on-track"
              className="card-soft inline-flex items-center gap-3 rounded-full px-4 py-2"
            >
              <span className="text-[12.5px] font-semibold text-zinc-800">
                {adapted.onTrackPercent}% on track
              </span>
              <div className="w-20">
                <GradientBar
                  value={adapted.onTrackPercent}
                  testId="stat-on-track-bar"
                  height={5}
                />
              </div>
            </div>

            <div
              data-testid="stat-milestones"
              className="card-soft inline-flex items-center gap-2 rounded-full px-4 py-2"
            >
              <Flag className="h-3.5 w-3.5 text-zinc-500" strokeWidth={2.2} />
              <span className="text-[12.5px] font-semibold text-zinc-800">
                {adapted.milestones.length} milestones
              </span>
              <span className="text-zinc-300">·</span>
              <span className="text-[12.5px] font-semibold text-orange-700">
                {atRiskCount} at risk
              </span>
            </div>
          </div>

          <div className="mt-7">
            <PhaseStrip phases={adapted.phases} tasks={adapted.tasks} />
          </div>

          <div className="mt-8">
            {view === "timeline" ? (
              <TimelineCanvas roadmap={adapted} />
            ) : (
              <PhasesView roadmap={adapted} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
